/**
 * Security utilities for input validation and content filtering
 */

// Maximum input lengths for different types of content
export const INPUT_LIMITS = {
  MESSAGE: 2000,
  NAME: 100,
  EMAIL: 254,
  PHONE: 20,
  NOTES: 1000,
} as const;

// Potentially harmful patterns to filter out
const HARMFUL_PATTERNS = [
  // Script injection attempts
  /<script[^>]*>.*?<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  
  // SQL injection patterns
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER)\b)/gi,
  /(\b(UNION|OR|AND)\s+\d+\s*=\s*\d+)/gi,
  
  // XSS patterns
  /<iframe[^>]*>/gi,
  /<object[^>]*>/gi,
  /<embed[^>]*>/gi,
  
  // Potential credential harvesting
  /\b(password|token|secret|key|credential)\s*[:=]\s*[\w\-_]+/gi,
] as const;

// Suspicious content patterns
const SUSPICIOUS_PATTERNS = [
  /\b(hack|exploit|vulnerability|bypass|injection)\b/gi,
  /\b(admin|root|super)\s+(password|login|access)\b/gi,
] as const;

/**
 * Sanitizes input by trimming whitespace and limiting length
 */
export function sanitizeInput(input: string, maxLength: number = INPUT_LIMITS.MESSAGE): string {
  if (typeof input !== 'string') {
    return '';
  }
  
  return input.trim().slice(0, maxLength);
}

/**
 * Validates input for potentially harmful content
 */
export function validateContent(input: string): { isValid: boolean; reason?: string } {
  if (!input || typeof input !== 'string') {
    return { isValid: false, reason: 'Invalid input type' };
  }

  // Check for harmful patterns
  for (const pattern of HARMFUL_PATTERNS) {
    if (pattern.test(input)) {
      return { isValid: false, reason: 'Content contains potentially harmful patterns' };
    }
  }

  // Check for suspicious patterns (warning level)
  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (pattern.test(input)) {
      console.warn('Suspicious content detected:', input.substring(0, 100));
      // Allow but log for monitoring
    }
  }

  return { isValid: true };
}

/**
 * Validates email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates phone number format (basic validation)
 */
export function validatePhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
}

/**
 * Escapes HTML entities to prevent XSS
 */
export function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  
  return text.replace(/[&<>"']/g, (match) => map[match]);
}

/**
 * Rate limiting check (enhanced client-side implementation)
 * For production, use server-side rate limiting
 */
const rateLimitStorage = new Map<string, number[]>();

export function checkRateLimit(
  identifier: string, 
  maxRequests: number = 10, 
  windowMs: number = 60000
): boolean {
  const now = Date.now();
  const requests = rateLimitStorage.get(identifier) || [];
  
  // Remove old requests outside the window
  const validRequests = requests.filter(time => now - time < windowMs);
  
  if (validRequests.length >= maxRequests) {
    return false; // Rate limited
  }
  
  // Add current request
  validRequests.push(now);
  rateLimitStorage.set(identifier, validRequests);
  
  return true; // Not rate limited
}

/**
 * Logs security events to server-side storage for monitoring
 */
export async function logSecurityEvent(
  event: string,
  details: Record<string, any> = {},
  severity: 'low' | 'medium' | 'high' = 'medium'
): Promise<void> {
  const logEntry = {
    event_type: event,
    details,
    severity,
    user_agent: navigator?.userAgent || 'unknown',
    url: window?.location?.href || 'unknown'
  };
  
  // Log to console for development
  console.warn('Security Event:', logEntry);
  
  // Send to server-side security_events table
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    await supabase
      .from('security_events')
      .insert(logEntry);
  } catch (error) {
    console.error('Failed to log security event:', error);
    
    // Fallback to localStorage for critical events only
    if (severity === 'high') {
      const criticalEvents = JSON.parse(localStorage.getItem('security_events') || '[]');
      criticalEvents.push({ ...logEntry, timestamp: new Date().toISOString() });
      // Keep only last 10 events as fallback
      if (criticalEvents.length > 10) {
        criticalEvents.splice(0, criticalEvents.length - 10);
      }
      localStorage.setItem('security_events', JSON.stringify(criticalEvents));
    }
  }
}

/**
 * Validates file upload security
 */
export function validateFileUpload(file: File): { isValid: boolean; reason?: string } {
  // Allowed file types
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, reason: 'File type not allowed' };
  }
  
  if (file.size > maxSize) {
    return { isValid: false, reason: 'File size too large' };
  }
  
  // Check for suspicious file names
  if (/[<>:"/\\|?*]/.test(file.name)) {
    return { isValid: false, reason: 'Invalid file name' };
  }
  
  return { isValid: true };
}